/**
 * "Home's first screen is ready", for Telegram's `WebApp.ready()`.
 *
 * Telegram keeps its own loading placeholder over the Mini App until the page
 * calls `ready()`. Calling it on mount uncovered a Home whose banner, notice
 * and images then arrived one by one. Holding it until those are in lets the
 * placeholder hand over to a finished screen instead.
 *
 * The wait is capped at FIRST_SCREEN_BUDGET_MS counted from page open (the
 * navigation start `performance.now()` measures from), not from mount: once
 * the budget is spent Telegram is released whatever is still loading. A cold
 * first open that spends the whole budget on JS therefore gets no extra wait
 * at all. Only a page load that starts on Home waits; any other entry route
 * releases immediately. Outside Telegram nothing calls this.
 */

const FIRST_SCREEN_BUDGET_MS = 1000;

let markReady: () => void = () => {};
const homeReady = new Promise<void>((resolve) => {
  markReady = resolve;
});
let homeReadyMarked = false;

// Visible in DevTools' Performance panel and via
// performance.getEntriesByName(), so the real timing can be read on a device.
function mark(name: string): void {
  try { performance.mark(name); } catch { /* User Timing unavailable */ }
}

let settled: Promise<void> | null = null;

/**
 * True when this page load opened on Home. Read on first use, not at import:
 * main.tsx rewrites a Telegram `#tgWebAppData=…` hash to `#/` before React
 * mounts, and that hash means Home too.
 */
function startedOnHome(): boolean {
  const h = window.location.hash;
  return !h.startsWith('#/') || h === '#/' || h.startsWith('#/?');
}

/** Called by HomeV2 once its first screen has loaded. Idempotent. */
export function markHomeFirstScreenReady(): void {
  if (homeReadyMarked) return;
  homeReadyMarked = true;
  mark('hg:home-first-screen-ready');
  markReady();
}

/**
 * Resolves when Home's first screen is ready or the budget runs out,
 * whichever is first. The same promise for the whole page load.
 */
export function whenFirstScreenSettled(): Promise<void> {
  if (!settled) {
    settled = startedOnHome()
      ? Promise.race([
          homeReady,
          new Promise<void>((resolve) =>
            setTimeout(resolve, Math.max(0, FIRST_SCREEN_BUDGET_MS - performance.now())),
          ),
        ])
      : Promise.resolve();
    void settled.then(() => mark('hg:first-screen-settled'));
  }
  return settled;
}
